import { City, Country, PhoneNumberType,DocumentType, InitialEncounterSourceType, PaymentType, TransportationType, BodyStyleType, Manufacturer, Color, Service, SubService, State, Region, Continent, AssetType, RateType, OrganizationSpecialty, Language, OverTimeRateType, ResourceType, EquipmentType, ServiceCategoryType } from "./Lookups";
import { UserDTO, ContactSearchResultDTO, OperatorType, ExperienceLevel, Contact } from "./User";
import { BaseDocument } from "../components/document/DocumentManager";
export type Organization = {
    organizationId:number | null,
    name: string;
    photo?: string | null;
    photoFile: string | null,
    type:OrganizationType,
    relationshipType: OrganizationRelationshipType | null,
    notes?: string | null;
    sourceType?:InitialEncounterSourceType|null;
    sourceDetails?: string | null;
    socialMediaInfo?: Record<string, string | null>;
    organizationEmails:OrganizationEmail[],
    organizationPhoneNumbers:OrganizationPhoneNumber[],
    organizationAddresses:OrganizationAddress[],
    organizationDocuments: BaseDocument[],
    overtimeRate: number | null,
    dayInHours: number | null,
    createdBy:number;
    status:OrganizationStatus;
    assignedUser:UserDTO | null;
    contacts: ContactSearchResultDTO[];
    paymentInfo:OrganizationPaymentInfo[];
    organizationReferenceNumber: string,
    internalNotes?:string;
    parentOrganization: Organization | null;
    organizationSpecialty?: OrganizationSpecialty | null,
    isProfileCompleted?: boolean| null;
    sourceUserDetail?:Contact | null;
    photoFileKey?: string;
    originLeadId?: number | null;
    originLeadReference?: string | null;
    operationalProviderRating?: number | null;
    operationalProviderCount?: number;
    financialProviderRating?: number | null;
    financialProviderCount?: number;
    operationalClientRating?: number | null;
    operationalClientCount?: number;
    financialClientRating?: number | null;
    financialClientCount?: number;
    providerReferenceNumber?: string | null;
    clientReferenceNumber?: string | null;
    isFreeLancer?: boolean;
    /**
     * Marks OUR own issuing entity. When true (or when type === Company) the
     * Bank payment card exposes the Issuing-entity group (legal name + tax id)
     * and `issuer=true` is sent to the /template fetch. Optional so the BE can
     * surface it later without breaking existing callers.
     */
    isSystem?: boolean | null;

    // Concurrency / idempotency. Optional so legacy code keeps compiling
    // and the BE auto-merge only runs when these are supplied.
    version?: number;
    clientMutationId?: string;
    forceOverwrite?: boolean;
    overwriteReason?: string;

    /** ISO timestamps for the record page footer (BE skips both on save). */
    createdOn?: string | null;
    updatedAt?: string | null;
}
export enum OrganizationType {
    Client = 'client',
    Provider = 'provider',
    Hybrid = 'hybrid',
    Company  = 'company',
}
export enum OrganizationRelationshipType {
    Direct = 'direct',
    Indirect = 'indirect',
    Competitor = 'competitor',
}

export enum OrganizationStatus{
    Potential = 'Potential',
    Active = 'Active',
    Suspended = 'Suspended',
    Terminated = 'Terminated',
}

export type OrganizationEmail = {
    organizationEmailId: number| null;
    email:string;
    isPrimary: boolean | null;
    isValid: boolean | null;
    deletedAt: Date | null;
    version?: number;
};

export type OrganizationPaymentInfo = {
    paymentInfoId: number| null;
    organization:Organization | null;
    isPreferred: boolean;
    paymentType: PaymentType | null;
    deletedAt: Date|null;
    /**
     * Non-Bank method extras ONLY (Cliq / Exchange center / Cheque / Cash /
     * Indirect). The Bank method no longer writes here — its fields are the
     * typed top-level columns below so finance data stays queryable + FK-clean.
     */
    details:PaymentDetails;
    organizationPaymentInfoDocuments:BaseDocument[];
    referralUserDetail:Contact | null;
    /**
     * Account currency. First-class on the method so the finance module can
     * match the right account to a document. Bound by the currency picker and
     * sent as a TOP-LEVEL field (currencyId) — matches the BE DTO field names.
     */
    currencyId?: number | null;
    currencyCode?: string | null;
    /**
     * For Bank methods: the linked OrganizationAddress that designates the
     * branch address (country + city come from it). Sent as a top-level field.
     */
    organizationAddressId?: number | null;

    // ── Typed Bank-method columns ──────────────────────────────────────────
    // These mirror the BE OrganizationPaymentInfoDTO typed fields one-for-one.
    // The Bank template's field `name`s EQUAL these prop names, so a Bank
    // template field binds directly to paymentInfo[name] (NOT details[name]).
    // Non-Bank methods leave them null and use `details` instead.
    /** Account group: beneficiary / account-holder name. */
    accountName?: string | null;
    /** Account group: bank name. */
    bankName?: string | null;
    /** Account group: account number. */
    accountNumber?: string | null;
    /** Account group: IBAN (mod-97 validated). */
    iban?: string | null;
    /** Account group: SWIFT / BIC (8 or 11 char validated). */
    swiftCode?: string | null;
    /** Account group: bank / branch street address. */
    bankAddress?: string | null;
    /** Account group: bank country FK (common.country). First-class control. */
    countryId?: number | null;
    /** Intermediary group: correspondent bank name (international wires). */
    intermediaryBankName?: string | null;
    /** Intermediary group: correspondent bank SWIFT / BIC. */
    intermediarySwift?: string | null;
    /** Issuing-entity group (our own org only): legal name on the invoice. */
    legalName?: string | null;
    /** Issuing-entity group: tax-id label (TRN / VAT/Registration #). */
    taxIdLabel?: string | null;
    /** Issuing-entity group: tax-id value. */
    taxIdValue?: string | null;
    /**
     * GL cash/bank account FK (finance.account). The Chart-of-Accounts ledger
     * account that payments through this method post to. First-class control.
     * Sent as a top-level field (glAccountId) — matches the BE DTO field names.
     */
    glAccountId?: number | null;
    /** GL account code mirror (e.g. "1010"). Read-only convenience from BE. */
    glAccountCode?: string | null;
    /** GL account name mirror (e.g. "Bank"). Read-only convenience from BE. */
    glAccountName?: string | null;
};

/** Free-form, template-driven detail blob persisted as jsonb on the BE. */
export type PaymentDetails = {
    [key: string]: string | number | boolean | null;
};

/**
 * The payment-method template is now the VALIDATION CONTRACT shared with the
 * BE (PaymentTypeTemplateService). Each field carries rendering + validation
 * + grouping metadata so the FE manager and the BE save path enforce the same
 * rules (no malformed IBAN/SWIFT, no missing required field).
 */
export type PaymentTemplates ={
    [key: string]: PaymentTemplateField[];
}
export type PaymentTemplateFieldType = "text" | "select" | "currency" | "country" | "checkbox" | "file";
export type PaymentTemplateFieldValidation = "iban" | "swift" | "phone" | "email";
export type PaymentTemplateFieldGroup = "account" | "intermediary" | "entity";
export type PaymentTemplateField = {
    name: string;
    label: string;
    type: PaymentTemplateFieldType;
    required?: boolean;
    validation?: PaymentTemplateFieldValidation;
    group?: PaymentTemplateFieldGroup;
    placeholder?: string;
    hint?: string;
    options?: { value: string; label: string }[];
}
/** @deprecated kept for back-compat; use PaymentTemplateField. */
export type PaymentTemplate = PaymentTemplateField;


export type OrganizationPhoneNumber = {
    organizationPhoneNumberId: number| null;
    phoneNumber:string;
    isPrimary: boolean | null;
    type: PhoneNumberType | null;
    isValid: boolean | null;
    notes: string | null;
    deletedAt: Date | null;
    version?: number;
};
export type OrganizationAddress = {
    organizationAddressId: number| null;
    country:Country | null;
    state:State | null;
    city: City | null;
    isPrimary: boolean | null;
    isValid: boolean | null;
    address:string | null;
    deletedAt: Date | null;
    version?: number;
}

export type OrganizationAssetType = {
    organizationAssetTypeId:number | null,
    type?: AssetType | null;
    bodyStyle?: BodyStyleType | null;
    costRange:string;
    actualCostRange?:string;
    quantity: number;
    actualQuantity?: number;
    organization:Organization;
    createdBy:number;
    notes: string;
};
/**
 * Asset-kind discriminator. Drives:
 *   - which fields the provider Asset form requires
 *   - where the asset surfaces in pickers across the app
 *   - which lookup column governs the cross-resource filter
 */
export enum AssetKind {
    Vehicle = "vehicle",
    Equipment = "equipment",
    Other = "other",
}

/**
 * Equipment-only lifecycle. Surfaced as a chip in the picker.
 * `out_of_service` is a soft state — the row stays in inventory but
 * is hidden from default-scope pickers until restored.
 */
export enum AssetCondition {
    Serviceable = "serviceable",
    Worn = "worn",
    Damaged = "damaged",
    Retired = "retired",
    OutOfService = "out_of_service",
}

export type OrganizationAsset = {
    assetId:number | null,
    type: AssetType | null;
    transportationType?: TransportationType | null;
    bodyStyle?: BodyStyleType | null;
    manufacturer?: Manufacturer | null;
    color?: Color | null;
    numberOfSeats: number | null;
    trackingNumber: string | null;
    createdBy:number;
    model: string | null;
    manufactureYear: string | null;
    organization:Organization;
    organizationAssetDocuments:BaseDocument[];
    organizationAssetImages:BaseDocument[];
    referenceNumber:string;
    organizationAssetType?: OrganizationAssetType;
    cost:number,
    notes: string | null,
    averageRating?: number | null;
    totalRatings?: number;
    /** Sprint 1: location binding + kind discriminator + equipment
     *  metadata. Country is required at save time (the form blocks
     *  submission without it). City is optional — used by the
     *  Mission asset pickers as a same-city sort hint only. */
    countryId?: number | null;
    countryName?: string | null;
    cityId?: number | null;
    cityName?: string | null;
    kind?: AssetKind;
    serialNumber?: string | null;
    assetTag?: string | null;
    condition?: AssetCondition | null;
    calibrationDueAt?: Date | string | null;
    /** Equipment-only — FK + denormalized name from the EquipmentType
     *  catalog. Only set when `kind === 'equipment'`. */
    equipmentTypeId?: number | null;
    equipmentTypeName?: string | null;
    /** Last-write timestamp echoed back as the optimistic-lock token on the
     *  finance pricing PATCH. Read-only. */
    updatedAt?: Date | string | null;
};


export type OrganizationService = {
    organizationServiceId:number | null,
    organization:Organization;
    service: Service | null;
    subService: SubService | null;
    organizationServiceDetails: OrganizationServiceDetail[];
    organizationServiceDocuments:BaseDocument[];
    referenceNumber:string;
    createdBy:number;
    notes:string;
    /** List-endpoint glance counts (active resources / rates across the
     *  service's locations) — absent on the single-service GET. */
    resourceCount?: number;
    rateCount?: number;
};
export type OrganizationServiceDetail = {
    detailId:number | null,
    continent?: Continent | null,
    region: Region | null,
    country: Country | null;
    state: State |null;
    city: City | null;
    taxRate: number;
    notes: string;
    deletedAt: Date|null;
    costRange?:string;
    organizationServiceDetailAssets?:OrganizationServiceDetailAsset[];
};
export type OrganizationServiceDetailAsset = {
    assetId:number | null,
    notes: string;
    deletedAt: Date|null;
    /** Five-axis classification (bundle / vehicle / crew / equipment / other).
     *  Defaults to Bundle on new rows. */
    resourceType?: ResourceType;
    assetType?: AssetType | null;
    assetBodyStyle?: BodyStyleType | null;
    operatorType?: OperatorType | null;
    experienceLevel?: ExperienceLevel | null;
    crewHeadcount?: number;
    equipmentType?: EquipmentType | null;
    serviceCategoryType?: ServiceCategoryType | null;
    description?: string | null;
    organizationServiceDetailAssetPrices?:OrganizationServiceDetailAssetPrice[];
};
export type OrganizationServiceDetailAssetPrice ={
    priceId: number;
    cost: number;
    price: number;
    taxRate: number;
    totalCost: number;
    totalPrice: number;
    notes: string;
    rateType: RateType | null;
    deletedAt: Date | null;
    overtimeRate: number;
    dayInHours: number;
    minHours: number;
    overTimeRateType: OverTimeRateType;
    /**
     * Currency this row is quoted in. Optional in the type so
     * existing call sites (mission / mission-request fallbacks)
     * compile without a wholesale rewrite; the provider editor
     * always sets it and the BE enforces NOT NULL.
     */
    currencyId?: number | null;
    currencyCode?: string | null;
    currencySymbol?: string | null;
    /** Last-write timestamp echoed back as the optimistic-lock token on the
     *  finance pricing PATCH. Read-only. */
    updatedAt?: Date | string | null;
}

export type Currency = {
    currencyId: number;
    code: string;
    name: string;
    symbol: string | null;
}

export type OrganizationLanguage = {
    organizationLanguageId: number | null;
    language:Language;
    deletedAt: Date | null;
};